<script setup>
import { ref, computed, watch, inject } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { storeToRefs } from 'pinia';
import { useProductConfigurationStore } from '@/stores/productConfiguration';

const errorStore = inject('errorStore');

const attributesErrors = computed(() => {
    const errs = errorStore?.all?.attributes || [];
    return Array.isArray(errs) ? errs : [errs].filter(Boolean);
});

const hasAttributesError = computed(() => attributesErrors.value.length > 0);

const page = usePage();
const store = useProductConfigurationStore();
const storeRefs = storeToRefs(store);

const form = computed(() => storeRefs.form?.value ?? {});
const detail = computed(() => storeRefs.detail?.value ?? null);

const saving = ref(false);

const isClone = computed(() => page.url.endsWith('/clone'));

const isEdit = computed(() =>
    page.url.startsWith('/product-configurations/') &&
    !page.url.endsWith('/create') &&
    !isClone.value
);

const pageTitle = computed(() => {
    if (isEdit.value) {
        return 'label.product_configuration';
    }
    if (isClone.value) {
        return 'label.clone_product';
    }
    return 'label.new_product';
});

// Local form state
const localForm = ref({
    service_id: 1,
    title: '',
    description: '',
    is_active: true,
    product_data_source_id: null,
    attributes: [],
});

// Computed properties for attribute catalog and data sources
const attribute_catalog = computed(() => form.value?.attribute_catalog ?? []);
const data_sources = computed(() => form.value?.data_sources ?? []);

// Map product data to form structure
const mapProductToForm = (product) => ({
    service_id: product.service_id,
    title: isClone.value
        ? `Copy of ${product?.title ?? ''}`
        : (product?.title ?? ''),
    description: product?.description ?? '',
    is_active: !!product?.is_active,
    product_data_source_id: product?.product_data_source_id,
    attributes: Array.isArray(product?.configuration_attributes)
        ? product.configuration_attributes
        : [],
});

// Hydrate local form from detail data
const hydrateFromDetail = () => {
    if (!isEdit.value && !isClone.value) {
        return;
    }

    const product = detail.value?.product;
    if (!product) {
        return;
    }

    localForm.value = mapProductToForm(product);
};

watch(
    [() => detail.value?.product?.hash, isEdit, isClone],
    hydrateFromDetail,
    { immediate: true }
);

// Toggle attribute selection
const toggleAttribute = (attr) => {
    clearAttributesErrors();

    // Check if attribute is already selected
    const idx = localForm.value.attributes.findIndex(
        a => a.product_attribute_id === attr.id
    );

    if (idx >= 0) {
        localForm.value.attributes.splice(idx, 1);
        return;
    }

    // Add new attribute
    localForm.value.attributes.push({
        product_attribute_id: attr.id,
        is_enabled: true,
        sort_order: localForm.value.attributes.length + 1,
        mappings: [],
    });
};

const save = async () => {
    saving.value = true;

    try {
        if (isEdit.value) {
            const product = detail.value.product;
            await store.update(product.hash, localForm.value);
        } else {
            // Handle Create & Clone
            await store.store(localForm.value);
        }

        // Reset cached detail to prevent stale data on subsequent page visits
        store.detail = null;

        router.visit('/product-configurations');
    } finally {
        saving.value = false;
    }
};

const clearAttributesErrors = () => {
    if (!errorStore) {
        return;
    }

    errorStore.remove('attributes');

    // Remove nested attribute errors
    Object.keys(errorStore.all || {}).forEach((key) => {
        if (key === 'attributes' || key.startsWith('attributes.')) {
            errorStore.remove(key);
        }
    });
};

</script>

<template>
<MainContentLayout>
    <template #title>
        {{ $t(pageTitle) }}
    </template>

    <form class="grid gap-4" @submit.prevent="save">
        <StyledInput
            v-model="localForm.title"
            name="title"
            :label="$t('label.product_title')" />

        <StyledTextArea
            v-model="localForm.description"
            name="description"
            :label="$t('label.product_description')" />

        <StyledDropdown
            v-model="localForm.is_active"
            name="is_active"
            :label="$t('label.status')"
            :options="[
                { label: $t('label.active'), value: true },
                { label: $t('label.inactive'), value: false },
            ]" />

        <div class="w-full" :class="{ 'v-input--error': hasAttributesError }">
            <label
                class="block mb-2 font-semibold text-base"
                :class="hasAttributesError ? 'text-red-600' : 'text-gray-700'">
                {{ $t('label.rule_variable_selection') }}
            </label>

            <div
                class="w-full px-4 py-3 bg-white"
                :class="hasAttributesError ? 'border-b' : 'border-b border-gray-300'">

                <div class="grid gap-1">
                    <label
                        v-for="attr in attribute_catalog"
                        :key="attr.id"
                        class="flex items-center gap-2">
                        <input
                            type="checkbox"
                            class="h-4 w-4"
                            :checked="localForm.attributes.some(
                                a => a.product_attribute_id === attr.id
                            )"
                            @change="toggleAttribute(attr)">

                        <span class="text-base text-gray-800">
                            {{ attr.name }}
                        </span>
                    </label>
                </div>
            </div>

            <div
                v-if="attributesErrors.length"
                class="v-input__details pl-4"
                role="alert"
                aria-live="polite">
                <div class="v-messages">
                    <div
                        v-for="err in attributesErrors"
                        :key="err"
                        class="v-messages__message">
                        {{ err }}
                    </div>
                </div>
            </div>
            
        </div>

        <StyledDropdown
            v-model="localForm.product_data_source_id"
            name="product_data_source_id"
            :label="$t('label.product_data_source')"
            :options="data_sources"
            item-title="name"
            item-value="id"
            clearable />

        <StyledSaveButton
            :loading="saving"
            :label="$t('label.save')" 
            @save="save" />
    </form>
</MainContentLayout>
</template>
